import React, { Component } from "react";
import { Button, Tooltip } from "antd";
import { UserAddOutlined, CheckCircleFilled, PlusOutlined } from "@ant-design/icons";

// BuyerProfileBar — renders at the very top of search results
// Three states:
//   1. Not logged in   → hero hook banner
//   2. Logged in       → profile tiles (load/create)
//   3. Profile active  → compact active tile + switch option

class BuyerProfileBar extends Component {
  render() {
    var userLogin = this.props.userLogin;
    var profiles = this.props.profiles || [];
    var activeProfileId = this.props.activeProfileId;
    var onLoad = this.props.onLoad;
    var onCreate = this.props.onCreate;
    var onClearProfile = this.props.onClearProfile;

    // ── State 1: Not logged in ───────────────────────────────────────────────
    if (!userLogin) {
      return (
        <div style={{
          background: "linear-gradient(135deg, #2A3390 0%, #1A5C40 100%)",
          borderRadius: "12px",
          padding: "20px 24px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{ fontSize: "32px" }}>🏡</div>
            <div>
              <div style={{ color: "#D4922A", fontSize: "11px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>
                My Home Buddha — Healthy · Wealthy · Wise
              </div>
              <div style={{ color: "white", fontSize: "16px", fontWeight: "700", marginBottom: "4px" }}>
                See how well each home matches <em>your life</em>
              </div>
              <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                You're seeing preview scores based on your search filters.
                Create a free account to save your buyer profile and get enhanced match scoring with commute distances and AI lifestyle analysis.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Button
              href="/register"
              style={{
                background: "#D4922A", border: "none", color: "#2c2416",
                fontWeight: "800", borderRadius: "50px", padding: "0 22px", height: "40px",
              }}
            >
              Create Free Account
            </Button>
            <Button
              href="/login"
              style={{
                background: "transparent", border: "2px solid rgba(255,255,255,0.6)",
                color: "white", fontWeight: "700", borderRadius: "50px", padding: "0 22px", height: "40px",
              }}
            >
              Sign In
            </Button>
          </div>
        </div>
      );
    }

    // ── State 2: Logged in, profile tiles ───────────────────────────────────
    return (
      <div style={{ marginBottom: "16px" }}>
        <div style={{
          background: "#f8f9ff",
          border: "1.5px solid #e0e3f0",
          borderRadius: "12px",
          padding: "14px 18px",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: profiles.length > 0 ? "12px" : "0" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#2A3390" }}>
              {activeProfileId ? "Active Buyer Profile" : "Your Buyer Profiles"}
            </div>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={onCreate}
              style={{
                background: "#2A3390", border: "none", color: "white",
                borderRadius: "50px", fontWeight: "600", fontSize: "12px",
              }}
            >
              Create New Profile
            </Button>
          </div>

          {profiles.length === 0 ? (
            <div style={{ color: "#888", fontSize: "13px", padding: "8px 0" }}>
              No buyer profiles yet.{" "}
              <span
                onClick={onCreate}
                style={{ color: "#2A3390", fontWeight: "700", cursor: "pointer", textDecoration: "underline" }}
              >
                Create your first profile
              </span>
              {" "}to see personalized match scores with commute distances and AI analysis.
            </div>
          ) : (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
              {profiles.map(function(profile) {
                var isActive = profile.id === activeProfileId;
                var title = (profile.buyerPropertyDetails2 && profile.buyerPropertyDetails2.title) || "Buyer Profile";
                var towns = profile.buyerPropertyDetails && profile.buyerPropertyDetails.updatedInterestedTowns
                  ? profile.buyerPropertyDetails.updatedInterestedTowns.slice(0, 2).map(function(t) { return t.cityName; }).join(", ")
                  : "";
                var priceMin = profile.buyerPropertyDetails && profile.buyerPropertyDetails.minprice;
                var priceMax = profile.buyerPropertyDetails && profile.buyerPropertyDetails.maxprice;
                var priceLabel = priceMin || priceMax
                  ? "$" + ((priceMin || 0) / 1000).toFixed(0) + "k–$" + ((priceMax || 0) / 1000).toFixed(0) + "k"
                  : "Any price";

                return (
                  <Tooltip
                    key={profile.id}
                    title={isActive ? "Click to unload this profile" : "Load this profile for match scoring"}
                  >
                    <div
                      onClick={function() { isActive ? onClearProfile() : onLoad(profile); }}
                      style={{
                        background: isActive ? "#2A3390" : "white",
                        border: isActive ? "2px solid #2A3390" : "1.5px solid #d0d4e8",
                        borderRadius: "10px",
                        padding: "8px 14px",
                        cursor: "pointer",
                        minWidth: "140px",
                        transition: "all 0.2s",
                        position: "relative",
                      }}
                    >
                      {isActive && (
                        <CheckCircleFilled
                          style={{ color: "#D4922A", position: "absolute", top: "6px", right: "6px", fontSize: "14px" }}
                        />
                      )}
                      <div style={{
                        fontSize: "12px", fontWeight: "700",
                        color: isActive ? "white" : "#2A3390",
                        marginBottom: "2px",
                        paddingRight: isActive ? "16px" : "0",
                      }}>
                        {title.length > 22 ? title.slice(0, 22) + "…" : title}
                      </div>
                      {towns ? (
                        <div style={{ fontSize: "11px", color: isActive ? "rgba(255,255,255,0.75)" : "#888" }}>
                          {towns}
                        </div>
                      ) : null}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "4px" }}>
                        <div style={{ fontSize: "11px", color: isActive ? "#D4922A" : "#4A7C59", fontWeight: "600" }}>
                          {priceLabel}
                        </div>
                        <span
                          onClick={function(e) {
                            e.stopPropagation();
                            if (profile.isLifestyleProfile) {
                              this.props.onEditLifestyle && this.props.onEditLifestyle();
                            } else {
                              window.location.href = "/add/buyerform/" + profile.id;
                            }
                          }.bind(this)}
                          style={{ fontSize: "10px", color: isActive ? "rgba(255,255,255,0.7)" : "#2A3390", textDecoration: "underline", cursor: "pointer", marginLeft: "6px" }}
                        >
                          Edit
                        </span>
                      </div>
                    </div>
                  </Tooltip>
                );
              }.bind(this))}
            </div>
          )}
        </div>

        {activeProfileId && (
          <div style={{
            marginTop: "8px",
            background: "linear-gradient(90deg, #eef0ff, #f0fff4)",
            border: "1px solid #c8d0f0",
            borderRadius: "8px",
            padding: "8px 14px",
            fontSize: "12px",
            color: "#2A3390",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}>
            <CheckCircleFilled style={{ color: "#4A7C59" }} />
            Match scores active — click any listing for your full score breakdown + AI lifestyle analysis
          </div>
        )}
      </div>
    );
  }
}

export default BuyerProfileBar;